import React, { useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';

// ─── Toolbar primitives ────────────────────────────────────────────────────────
function TBtn({ onClick, active, title, children, danger }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick?.(); }}
      title={title}
      className={['rich-toolbar-btn', active ? 'active' : '', danger ? 'danger' : ''].filter(Boolean).join(' ')}
    >
      {children}
    </button>
  );
}
function Sep() { return <span className="rich-toolbar-sep" />; }
function Label({ children }) { return <span className="rich-toolbar-label">{children}</span>; }

// ─── RichTextEditor ────────────────────────────────────────────────────────────
// Props:
//   value          — HTML string (controlled)
//   onChange       — (html: string) => void
//   onImageUpload  — async (File) => string (returns public URL); omit to disable image button
//   readOnly       — render content without toolbar and non-editable
//   minHeight      — minimum editor body height in px (default 320)
export default function RichTextEditor({
  value,
  onChange,
  onImageUpload,
  readOnly = false,
  minHeight = 320,
  stickyTop = 0,
}) {
  const prevValueRef      = useRef(value);
  // Guard flag: suppress onUpdate callbacks fired during programmatic setContent
  // (tiptap v3 fires onUpdate even when emitUpdate=false)
  const settingContentRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ code: false, codeBlock: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor: e }) => {
      if (settingContentRef.current) return;
      const html = e.getHTML();
      prevValueRef.current = html;
      onChange?.(html);
    },
  });

  // Sync when parent changes the value externally (e.g. switching sections)
  useEffect(() => {
    if (!editor || readOnly) return;
    if (value !== prevValueRef.current) {
      settingContentRef.current = true;
      editor.commands.setContent(value || '', false);
      prevValueRef.current = value;
      // Reset after any synchronous onUpdate tiptap may fire during setContent
      setTimeout(() => { settingContentRef.current = false; }, 0);
    }
  }, [editor, value, readOnly]);

  const handleLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL:', prev || 'https://');
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim(), target: '_blank' }).run();
    }
  }, [editor]);

  const handleImage = useCallback(() => {
    if (!editor || !onImageUpload) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await onImageUpload(file);
        if (url) editor.chain().focus().setImage({ src: url }).run();
      } catch (err) {
        alert('Image upload failed: ' + (err.message || 'Unknown error'));
      }
    };
    input.click();
  }, [editor, onImageUpload]);

  // Read-only mode — render as static HTML, no editor overhead
  if (readOnly) {
    return (
      <div
        className="rich-editor-readonly ql-editor"
        style={{ minHeight, padding: '12px 16px' }}
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
    );
  }

  const inTable = editor?.isActive('table');

  return (
    <div className="rich-editor-wrapper">
      {/* ── Toolbar ── */}
      <div className="rich-editor-toolbar" style={stickyTop !== 0 ? { top: stickyTop } : undefined}>

        {/* Block format */}
        <TBtn
          title="Normal paragraph"
          active={editor?.isActive('paragraph') && !editor.isActive('heading')}
          onClick={() => editor?.chain().focus().setParagraph().run()}
        >¶</TBtn>
        <TBtn title="Heading 1" active={editor?.isActive('heading', { level: 1 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>H1</TBtn>
        <TBtn title="Heading 2" active={editor?.isActive('heading', { level: 2 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</TBtn>
        <TBtn title="Heading 3" active={editor?.isActive('heading', { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>H3</TBtn>

        <Sep />

        {/* Inline marks */}
        <TBtn title="Bold (Ctrl+B)" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}><b>B</b></TBtn>
        <TBtn title="Italic (Ctrl+I)" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}><i>I</i></TBtn>
        <TBtn title="Underline (Ctrl+U)" active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()}><u>U</u></TBtn>

        <Sep />

        {/* Alignment */}
        <TBtn title="Align left"   active={editor?.isActive({ textAlign: 'left' })}    onClick={() => editor?.chain().focus().setTextAlign('left').run()}>≡L</TBtn>
        <TBtn title="Align center" active={editor?.isActive({ textAlign: 'center' })}  onClick={() => editor?.chain().focus().setTextAlign('center').run()}>≡C</TBtn>
        <TBtn title="Align right"  active={editor?.isActive({ textAlign: 'right' })}   onClick={() => editor?.chain().focus().setTextAlign('right').run()}>≡R</TBtn>
        <TBtn title="Justify"      active={editor?.isActive({ textAlign: 'justify' })} onClick={() => editor?.chain().focus().setTextAlign('justify').run()}>≡J</TBtn>

        <Sep />

        {/* Lists */}
        <TBtn title="Ordered list" active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1.</TBtn>
        <TBtn title="Bullet list" active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()}>•</TBtn>

        <Sep />

        {/* Insert */}
        <TBtn title="Blockquote" active={editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()}>"</TBtn>
        <TBtn title="Insert / edit link" active={editor?.isActive('link')} onClick={handleLink}>🔗 Link</TBtn>
        {onImageUpload && (
          <TBtn title="Insert image" onClick={handleImage}>🖼 Image</TBtn>
        )}
        <TBtn title="Insert 3×3 table" onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
          ⊞ Table
        </TBtn>

        <Sep />

        {/* History */}
        <TBtn title="Undo (Ctrl+Z)" onClick={() => editor?.chain().focus().undo().run()}>↩</TBtn>
        <TBtn title="Redo (Ctrl+Y)" onClick={() => editor?.chain().focus().redo().run()}>↪</TBtn>

        <Sep />

        {/* Remove formatting */}
        <TBtn title="Remove all formatting" onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}>Tx</TBtn>

        {/* Table management — appears only when cursor is inside a table */}
        {inTable && (
          <>
            <Sep />
            <Label>Table:</Label>
            <TBtn title="Add row above" onClick={() => editor?.chain().focus().addRowBefore().run()}>+↑ Row</TBtn>
            <TBtn title="Add row below" onClick={() => editor?.chain().focus().addRowAfter().run()}>+↓ Row</TBtn>
            <TBtn title="Delete row" onClick={() => editor?.chain().focus().deleteRow().run()}>− Row</TBtn>
            <Sep />
            <TBtn title="Add column left" onClick={() => editor?.chain().focus().addColumnBefore().run()}>+← Col</TBtn>
            <TBtn title="Add column right" onClick={() => editor?.chain().focus().addColumnAfter().run()}>+→ Col</TBtn>
            <TBtn title="Delete column" onClick={() => editor?.chain().focus().deleteColumn().run()}>− Col</TBtn>
            <Sep />
            <TBtn title="Delete entire table" danger onClick={() => editor?.chain().focus().deleteTable().run()}>✕ Table</TBtn>
          </>
        )}
      </div>

      {/* ── Content area ── */}
      <EditorContent editor={editor} className="rich-editor-content" style={{ minHeight }} />
    </div>
  );
}
